import { useEffect, useState } from 'react';
import { UserProfile, useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Loader2 } from 'lucide-react';
import { apiCall } from '@/lib/api';

interface OrgSettings {
  organization_id: string;
  block_checkin_on_expired_subscription: 0 | 1 | boolean;
  block_checkin_on_no_daypass:           0 | 1 | boolean;
  block_checkin_on_overdue_balance:      0 | 1 | boolean;
  warn_low_balance_days:                 number;
  warn_overdue_after_days:               number;
  low_stock_threshold:                   number;
}

function asBool(v: 0 | 1 | boolean): boolean {
  return v === true || v === 1;
}

export default function Settings() {
  const { getToken } = useAuth();
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const token = await getToken();
        const s = await apiCall<OrgSettings>('/v1/orgs/settings', { bearerToken: token });
        if (!cancel) setSettings(s);
      } catch (e) {
        if (!cancel) setErr((e as Error).message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [getToken]);

  async function save(patch: Partial<OrgSettings>) {
    setSaving(true);
    setErr(null);
    try {
      const token = await getToken();
      const next = await apiCall<OrgSettings>('/v1/orgs/settings', {
        method: 'PATCH',
        body: patch,
        bearerToken: token,
      });
      setSettings(next);
      setSavedAt(Date.now());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Account, security, and workspace preferences.
        </p>
      </div>

      {/* Check-in policy — per-gym blocking & warning thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Check-in policy</CardTitle>
          <CardDescription>
            What to do at the kiosk when a member is past due, out of day passes, or has an expired subscription.
            Suspended and inactive members are always blocked.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading current policy…
            </div>
          ) : err && !settings ? (
            <div className="text-sm text-red-600">{err}</div>
          ) : settings ? (
            <>
              <ToggleRow
                label="Block on expired subscription"
                hint="Member on a monthly plan with no active subscription and no payment this month is rejected at the kiosk."
                checked={asBool(settings.block_checkin_on_expired_subscription)}
                disabled={saving}
                onChange={(v) => save({ block_checkin_on_expired_subscription: v })}
              />
              <ToggleRow
                label="Block when day-pass balance hits zero"
                hint="Visit-based members with 0 days remaining are rejected. Recommended."
                checked={asBool(settings.block_checkin_on_no_daypass)}
                disabled={saving}
                onChange={(v) => save({ block_checkin_on_no_daypass: v })}
              />
              <ToggleRow
                label="Block on overdue monthly dues"
                hint="Active member missed this month's payment past the grace period below."
                checked={asBool(settings.block_checkin_on_overdue_balance)}
                disabled={saving}
                onChange={(v) => save({ block_checkin_on_overdue_balance: v })}
              />
              <NumberRow
                label="Warn when day-pass balance ≤"
                hint="Member sees 'only N passes left' banner; staff dashboard surfaces a chip."
                suffix="passes"
                value={settings.warn_low_balance_days}
                disabled={saving}
                onCommit={(n) => save({ warn_low_balance_days: n })}
              />
              <NumberRow
                label="Overdue grace period"
                hint="Don't flag a member overdue until this many days into the month."
                suffix="days into month"
                value={settings.warn_overdue_after_days}
                disabled={saving}
                onCommit={(n) => save({ warn_overdue_after_days: n })}
              />
              <NumberRow
                label="Low-stock alert threshold"
                hint="Products with stock at/below this number show in the dashboard's low-stock list."
                suffix="units"
                value={settings.low_stock_threshold}
                disabled={saving}
                onCommit={(n) => save({ low_stock_threshold: n })}
              />
              {err && <div className="text-sm text-red-600">{err}</div>}
              {savedAt && !err && (
                <div className="text-xs text-muted-foreground">Saved.</div>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Workspace / coming-soon section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Workspace</CardTitle>
          <CardDescription>
            Name, branding, and notification preferences. These will populate as features land.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Workspace name + logo</div>
              <div className="text-xs text-muted-foreground">Branding shown across the dashboard.</div>
            </div>
            <Button variant="outline" size="sm" disabled>Coming soon</Button>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Notifications</div>
              <div className="text-xs text-muted-foreground">Email / SMS preferences for owners and members.</div>
            </div>
            <Button variant="outline" size="sm" disabled>Coming soon</Button>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">API access</div>
              <div className="text-xs text-muted-foreground">Issue tokens for integrations and exports.</div>
            </div>
            <Button variant="outline" size="sm" disabled>Coming soon</Button>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Marketing site</div>
              <div className="text-xs text-muted-foreground">membersyncpro.com — the public landing.</div>
            </div>
            <Button asChild variant="ghost" size="sm">
              <a href="https://membersyncpro.com" target="_blank" rel="noreferrer">
                Open <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clerk-hosted account profile (email, password, 2FA, sessions, etc.) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account</CardTitle>
          <CardDescription>
            Email, password, two-factor auth, and active sessions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Clerk's drop-in profile UI; matches the rest of the auth surface */}
          <div className="-mx-2">
            <UserProfile routing="virtual" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ToggleRow({
  label, hint, checked, disabled, onChange,
}: { label: string; hint: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-md border p-3 gap-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 accent-current"
        />
        <span className="ml-2 text-xs text-muted-foreground">{checked ? 'On' : 'Off'}</span>
      </label>
    </div>
  );
}

function NumberRow({
  label, hint, suffix, value, disabled, onCommit,
}: { label: string; hint: string; suffix: string; value: number; disabled: boolean; onCommit: (n: number) => void }) {
  const [draft, setDraft] = useState<string>(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  return (
    <div className="flex items-center justify-between rounded-md border p-3 gap-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const n = Math.max(0, Math.floor(Number(draft) || 0));
            if (n !== value) onCommit(n);
          }}
          className="w-20 rounded-md border px-2 py-1 text-sm text-right"
        />
        <span className="text-xs text-muted-foreground whitespace-nowrap">{suffix}</span>
      </div>
    </div>
  );
}
